import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../store'
import { clearUnread } from '../../store/slices/notificationSlice'
import { notificationApi } from '../../api/notificationApi'
import { Notification } from '../../types'
import { toast } from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'
import { Bell, CheckCheck, Loader2 } from 'lucide-react'

const TYPE_COLOR: Record<string, string> = {
  JOB_APPROVED:                   'bg-green-100 text-green-600',
  JOB_REJECTED:                   'bg-red-100 text-red-600',
  APPLICATION_RECEIVED:           'bg-blue-100 text-blue-600',
  REFERRAL_CREATED:               'bg-purple-100 text-purple-600',
  CONNECTION_REQUEST_RECEIVED:    'bg-yellow-100 text-yellow-600',
  CONNECTION_REQUEST_ACCEPTED:    'bg-green-100 text-green-600',
  CHAT_REQUEST_RECEIVED:          'bg-indigo-100 text-indigo-600',
  ESCROW_REFUNDED:                'bg-teal-100 text-teal-600',
  BROADCAST:                      'bg-gray-100 text-gray-600',
}

export default function NotificationsPage() {
  const dispatch = useAppDispatch()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading]             = useState(true)
  const [markingAll, setMarkingAll]       = useState(false)
  const [page, setPage]                   = useState(0)
  const [totalPages, setTotalPages]       = useState(1)

  const load = async (p = 0) => {
    setLoading(true)
    try {
      const res = await notificationApi.getNotifications(p, 20)
      const paged = res.data.data
      setNotifications(p === 0 ? paged.content : (prev) => [...prev, ...paged.content])
      setTotalPages(paged.totalPages)
      setPage(p)
    } catch {
      toast.error('Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(0)
    // Mark all as read when page opens
    notificationApi.markAllRead().then(() => dispatch(clearUnread())).catch(() => {})
  }, [])

  const handleMarkAllRead = async () => {
    setMarkingAll(true)
    try {
      await notificationApi.markAllRead()
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
      dispatch(clearUnread())
      toast.success('All marked as read')
    } catch {
      toast.error('Failed to mark as read')
    } finally {
      setMarkingAll(false)
    }
  }

  const handleMarkOne = async (n: Notification) => {
    if (n.read) return
    try {
      await notificationApi.markRead(n.id)
      setNotifications((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x))
    } catch {
      // silent
    }
  }

  const unreadCount = notifications.filter((n) => !n.read).length

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-500 mt-0.5">{unreadCount} unread</p>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            disabled={markingAll}
            className="btn-secondary text-xs gap-1.5"
          >
            {markingAll ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <CheckCheck size={13} />
            )}
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div className="card divide-y divide-gray-100">
        {loading && notifications.length === 0 ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-primary-600" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-gray-400">
            <Bell size={36} className="mb-3 opacity-30" />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              onClick={() => handleMarkOne(n)}
              className={`flex gap-3 px-4 py-4 cursor-default transition-colors hover:bg-gray-50 ${!n.read ? 'bg-primary-50' : ''}`}
            >
              {/* Dot indicator */}
              <div className="flex-shrink-0 flex flex-col items-center pt-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${TYPE_COLOR[n.type] ?? 'bg-gray-100 text-gray-500'}`}
                >
                  <Bell size={14} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm font-semibold ${n.read ? 'text-gray-700' : 'text-gray-900'}`}>
                    {n.title}
                  </p>
                  {!n.read && (
                    <span className="flex-shrink-0 w-2 h-2 mt-1 rounded-full bg-primary-500" />
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{n.body}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                </p>
              </div>
            </div>
          ))
        )}

        {/* Load more */}
        {!loading && page + 1 < totalPages && (
          <div className="px-4 py-3 text-center">
            <button
              onClick={() => load(page + 1)}
              className="text-sm text-primary-600 font-medium hover:underline"
            >
              Load more
            </button>
          </div>
        )}

        {loading && notifications.length > 0 && (
          <div className="flex justify-center py-4">
            <Loader2 size={18} className="animate-spin text-primary-600" />
          </div>
        )}
      </div>
    </div>
  )
}
